import { ISelectItem } from "@components/inputs/select/Select.types";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import BindingContext from "@odata/BindingContext";
import { PrEmployeeEntity } from "@odata/GeneratedEntityTypes";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { getEldpAddYearOptions } from "@pages/payroll/Eldp/Eldp.utils";
import { getDefinitions as getEmployeeDefinitions } from "@pages/payroll/employee/Employee.def";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { FieldType } from "../../../enums";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";

export const YearPath = BindingContext.localContext("Year");
export const ELDP_FORM_ID = "GenerateEldpSpecialForm";

export function getGenerateEldpFormDefFactory(defaultYear: number): IGetDefinition {

    const getDefinition: IGetDefinition = (): IDefinition => {

        const form: IFormDef = {
            id: ELDP_FORM_ID,
            translationFiles: getDefinition.translationFiles,
            fieldDefinition: {
                [YearPath]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Eldp:Generate.Year"),
                    isRequired: true,
                    defaultValue: defaultYear,
                    fieldSettings: {
                        itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem[]> => {
                            return getEldpAddYearOptions(defaultYear ?? 0);
                        }
                    }
                }
            },
            groups: [
                {
                    id: "Common",
                    showGroupDivider: false,
                    rows: [
                        [{ id: YearPath }]
                    ]
                }
            ]
        };

        return {
            form
        };
    };

    getDefinition.translationFiles = ["Eldp", "Common"];
    // we don't add this definition even for testing as it is used with
    // no entity set and localContext, there is nothing to test against

    return getDefinition;
}

export function getGenerateEldpEmployeeDefFactory(defaultFilter: string): IGetDefinition {
    const getDefinition: IGetDefinition = (context: IAppContext): IDefinition => {

        const copyColumns = [
            PrEmployeeEntity.NumberOurs,
            PrEmployeeEntity.FirstName,
            PrEmployeeEntity.PrEmployeeStateCode,
            PrEmployeeEntity.ExternalNumber,
            PrEmployeeEntity.PersonalIdentificationNumber,
            PrEmployeeEntity.Email,
            PrEmployeeEntity.PhoneNumber,
        ];

        const employeeDef = getEmployeeDefinitions(context);
        const employeeFilterBarDef = employeeDef.table.filterBarDef[0];

        const table: ISplitPageTableDef = {
            preventStoreVariant: true,
            id: "GenerateEldpEmployeeSpecialTableView",
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [],
                filterDefinition: {},
                isValueHelp: true
            }],
            filter: defaultFilter,
            initialSortBy: [...employeeDef.table.initialSortBy],
            columns: [],
            columnDefinition: {},
        };

        const filterBarDef = table.filterBarDef[0];

        copyColumns.forEach(col => {
            if (employeeFilterBarDef.filterDefinition[col]) {
                filterBarDef.filterDefinition[col] = employeeFilterBarDef.filterDefinition[col];
                filterBarDef.defaultFilters.push(col);
            }
            if (employeeDef.table.columnDefinition[col]) {
                table.columnDefinition[col] = employeeDef.table.columnDefinition[col];
                table.columns.push(col);
            }
        });

        return {
            entitySet: employeeDef.entitySet,
            table,
            form: employeeDef.form,
        };
    };

    getDefinition.translationFiles = ["Eldp", ...getEmployeeDefinitions.translationFiles];
    // we don't add this definition even for testing as it is used with
    // no entity set and localContext, there is nothing to test against

    return getDefinition;
}